import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";
import { RootState } from "../../../../redux/store";
import {
  keyReturnFromDateSelected,
  keyReturnToDateSelected,
  setNextEnabled as setNextEnabledAction,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { EquipmentListProps } from "../../../../types/baseTypes";

type KeyHandoverReturnScheduleContextValue = {
  // State
  orderData: RootState["bookingCreation"]["orderData"];
  availabilities: RootState["bookingCreation"]["availabilities"];
  selectedGroup: EquipmentListProps | null;
  currency: string;
  nextEnabled: boolean;
  isLoading: boolean;
  // Actions
  setReturnFrom: (date: moment.Moment | null) => void;
  setReturnTo: (date: moment.Moment | null) => void;
  setReturnDates: (
    from: moment.Moment | null,
    to: moment.Moment | null
  ) => void;
  setNextEnabled: (enabled: boolean) => void;
};

const KeyHandoverReturnScheduleContext = createContext<
  KeyHandoverReturnScheduleContextValue | undefined
>(undefined);

export function KeyHandoverReturnScheduleProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useDispatch();

  const orderData = useSelector((state: RootState) => state.bookingCreation.orderData);
  const availabilities = useSelector((state: RootState) => state.bookingCreation.availabilities);
  const selectedGroup = useSelector(
    (state: RootState) => state.bookingCreation.selectedGroup
  ) as EquipmentListProps | null;
  const currency =
    useSelector((state: RootState) => state.tenantConfig.config.currency) || "EUR";
  const nextEnabled = useSelector((state: RootState) => state.bookingCreation.nextEnabled);

  const setReturnFrom = useCallback(
    (date: moment.Moment | null) => {
      dispatch(keyReturnFromDateSelected({ checkInDate: date }));
      const hasBoth = Boolean(date && orderData.keyReturnToDate);
      dispatch(setNextEnabledAction(hasBoth));
    },
    [dispatch, orderData.keyReturnToDate]
  );

  const setReturnTo = useCallback(
    (date: moment.Moment | null) => {
      dispatch(keyReturnToDateSelected({ checkOutDate: date }));
      const hasBoth = Boolean(orderData.keyReturnFromDate && date);
      dispatch(setNextEnabledAction(hasBoth));
    },
    [dispatch, orderData.keyReturnFromDate]
  );

  const setReturnDates = useCallback(
    (from: moment.Moment | null, to: moment.Moment | null) => {
      dispatch(keyReturnFromDateSelected({ checkInDate: from }));
      dispatch(keyReturnToDateSelected({ checkOutDate: to }));
      dispatch(setNextEnabledAction(Boolean(from && to)));
    },
    [dispatch]
  );

  const value = useMemo<KeyHandoverReturnScheduleContextValue>(
    () => ({
      orderData,
      availabilities,
      selectedGroup,
      currency,
      nextEnabled,
      isLoading: !Boolean(availabilities),
      setReturnFrom,
      setReturnTo,
      setReturnDates,
      setNextEnabled: (enabled: boolean) => dispatch(setNextEnabledAction(enabled)),
    }),
    [
      orderData,
      availabilities,
      selectedGroup,
      currency,
      nextEnabled,
      setReturnFrom,
      setReturnTo,
      setReturnDates,
      dispatch,
    ]
  );

  return (
    <KeyHandoverReturnScheduleContext.Provider value={value}>
      {children}
    </KeyHandoverReturnScheduleContext.Provider>
  );
}

export function useKeyHandoverReturnSchedule() {
  const ctx = useContext(KeyHandoverReturnScheduleContext);
  if (!ctx) {
    throw new Error(
      "useKeyHandoverReturnSchedule must be used within a KeyHandoverReturnScheduleProvider"
    );
  }
  return ctx;
}
